/**
 * Sucursales API Service
 * CRUD operations for branch management
 */

import { api } from "@/lib/api";

const SUCURSALES_URL = "/sucursales/";

// =============================================================================
// TYPES
// =============================================================================

export interface Sucursal {
  id: number;
  codigo: string;
  nombre: string;
  direccion: string;
  telefono: string;
  email?: string;
  horario_apertura?: string | null;
  horario_cierre?: string | null;
  opera_24_horas: boolean;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface SucursalCreateData {
  codigo: string;
  nombre: string;
  direccion: string;
  telefono: string;
  email?: string;
  horario_apertura?: string | null;
  horario_cierre?: string | null;
  opera_24_horas?: boolean;
}

export interface SucursalUpdateData {
  codigo?: string;
  nombre?: string;
  direccion?: string;
  telefono?: string;
  email?: string;
  horario_apertura?: string | null;
  horario_cierre?: string | null;
  opera_24_horas?: boolean;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Get all sucursales
 */
export async function getSucursales(): Promise<Sucursal[]> {
  const response = await api.get<Sucursal[] | { results: Sucursal[] }>(SUCURSALES_URL);
  if (Array.isArray(response.data)) {
    return response.data;
  }
  return response.data.results || [];
}

/**
 * Get a single sucursal by ID
 */
export async function getSucursal(id: number): Promise<Sucursal> {
  const response = await api.get<Sucursal>(`${SUCURSALES_URL}${id}/`);
  return response.data;
}

/**
 * Create a new sucursal
 */
export async function createSucursal(data: SucursalCreateData): Promise<Sucursal> {
  const response = await api.post<Sucursal>(SUCURSALES_URL, data);
  return response.data;
}

/**
 * Update an existing sucursal
 */
export async function updateSucursal(id: number, data: SucursalUpdateData): Promise<Sucursal> {
  const response = await api.patch<Sucursal>(`${SUCURSALES_URL}${id}/`, data);
  return response.data;
}

/**
 * Delete (deactivate) a sucursal
 */
export async function deleteSucursal(id: number): Promise<void> {
  await api.delete(`${SUCURSALES_URL}${id}/`);
}

/**
 * Activate a sucursal
 */
export async function activateSucursal(id: number): Promise<Sucursal> {
  const response = await api.post<Sucursal>(`${SUCURSALES_URL}${id}/activate/`);
  return response.data;
}

/**
 * Deactivate a sucursal
 */
export async function deactivateSucursal(id: number): Promise<Sucursal> {
  const response = await api.post<Sucursal>(`${SUCURSALES_URL}${id}/deactivate/`);
  return response.data;
}
